import { useEffect } from "react";

import { parseLinkHeader } from "@/core/utils/pagination";
import { Store } from "@/core/data_sources/store";
import { useService } from "@/core/utils/hooks";

import {
  getPlanDefinitionByQuery,
  getPlanDefinitions,
  type PlanDefinitionDetailQuery,
  type PlanDefinitionsQuery,
} from "../services";
import { PlanDefinitionsState } from "./state";

import type { PlanDefinitionsActions } from "./actions";
import type { PlanDefinitionsHooks } from "./hooks";

export class PlanDefinitionsStore extends Store<
  PlanDefinitionsState,
  PlanDefinitionsActions,
  PlanDefinitionsHooks
> {
  private static _instance: PlanDefinitionsStore;

  static get instance() {
    PlanDefinitionsStore._instance =
      PlanDefinitionsStore._instance || new PlanDefinitionsStore();
    return this._instance;
  }

  constructor() {
    super(
      new PlanDefinitionsState(),
      {
        setPlan: (item) => {
          this.state.setPlan(item);
        },
        setPlans: (list) => {
          this.state.setPlans(list);
        },
        setCount: (count) => {
          this.state.setCount(count);
        },
      },
      {
        usePlanDefinitionsStoreService: <Q extends PlanDefinitionsQuery>(
          q: Q,
        ) => {
          const { data, error, loading, query, setQuery } = useService(
            getPlanDefinitions,
            q,
          );
          useEffect(() => {
            if (error) {
              return;
            }
            if (!loading && data) {
              const pagination = parseLinkHeader(data.header);
              this.state.setPlans(data.data ?? []);
              this.state.setCount(pagination.count ?? data.data?.length ?? 0);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
        usePlanDefinitionDetailService: <Q extends PlanDefinitionDetailQuery>(
          q: Q,
        ) => {
          const { data, error, loading, query, setQuery } = useService(
            getPlanDefinitionByQuery,
            q,
          );
          useEffect(() => {
            if (error) {
              return;
            }
            if (!loading && data) {
              this.state.setPlan(data.data);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
      },
    );
  }
}
